import { isTimeZone } from 'src/kernel/time';

/**
 * User — an account somebody signs in with.
 *
 * THIN ON PURPOSE. The time zone preference is its first attribute because contexts need it and the
 * store was the only place to ask. The rest of what an account carries joins when an outcome needs it,
 * not for completeness.
 *
 * @domainObject entity
 * @identity userId
 * @invariant `timeZonePreference` is an IANA zone or null. Null means the user has not chosen one,
 *   and they are in the zone of whatever device they are using; the application layer resolves that,
 *   since reading the device is not a domain rule.
 * @typedef {Object} User
 * @property {string|null} userId - the account
 * @property {string|null} timeZonePreference - IANA zone the user chose, or null
 */

/**
 * A stored preference that is not a zone reads as no preference. Values written before anything
 * checked them can exist, and refusing to build the user would break every dated screen for them.
 *
 * @param {{userId?: string|null, timeZonePreference?: *}} parts
 * @returns {User}
 */
export const buildUser = ({ userId = null, timeZonePreference = null }) => Object.freeze({
  userId,
  timeZonePreference: isTimeZone(timeZonePreference) ? timeZonePreference : null,
});

/**
 * What a change may set: an IANA zone, or null to go back to following the device.
 *
 * @param {*} zone
 * @returns {boolean}
 */
export const isAllowedTimeZonePreference = (zone) => zone === null || isTimeZone(zone);
